<template>
  <div class="dashboard-header">
    <div class="team-info-header">
      <div class="team-logo-wrapper">
        <img
          v-if="team.logo_url"
          :src="team.logo_url"
          :alt="team.name"
          class="team-logo-large"
          decoding="async"
          @error="$emit('image-error', $event)"
        >
        <div v-else class="team-logo-placeholder">
          <i class="fa-solid fa-users"></i>
        </div>
      </div>
      <div class="team-details">
        <h1 class="header-title">{{ team.name }}</h1>
        <div class="team-meta">
          <span class="team-type">{{ team.team_type || 'Casual' }}</span>
          <span class="team-location">
            <i class="fa-solid fa-location-dot"></i>
            {{ team.county }}, {{ team.constituency }}
          </span>
          <span v-if="userRole === 'owner'" class="captain-badge">
            <i class="fa-solid fa-crown"></i> Team Owner
          </span>
          <span v-else-if="userRole === 'captain'" class="captain-badge">
            <i class="fa-solid fa-star"></i> Team Captain
          </span>
          <span class="team-status" :class="team.is_active ? 'active' : 'inactive'">
            {{ team.is_active ? 'Active' : 'Inactive' }}
          </span>
        </div>
        <p v-if="team.bio" class="team-bio">{{ team.bio }}</p>
        <div class="team-stats-quick">
          <span class="stat-item">
            <i class="fa-solid fa-futbol"></i>
            <strong>{{ team.total_matches || 0 }}</strong>
            <span>Matches</span>
          </span>
          <span class="stat-item">
            <i class="fa-solid fa-trophy"></i>
            <strong>{{ team.wins || 0 }}</strong>
            <span>Wins</span>
          </span>
          <span class="stat-item">
            <i class="fa-solid fa-users"></i>
            <strong>{{ team.member_count || 0 }}</strong>
            <span>Players</span>
          </span>
        </div>
      </div>
      <div class="team-actions">
        <button v-if="userRole === 'owner'" class="btn-action edit-btn" @click="$emit('edit-team')">
          <i class="fa-solid fa-pen-to-square"></i>
          Edit Team
        </button>
        <button v-if="userRole === 'owner'" class="btn-action invite-btn" @click="$emit('invite-players')">
          <i class="fa-solid fa-user-plus"></i>
          Invite Players
        </button>
        <button v-if="userRole === 'owner'" class="btn-action danger-btn" @click="$emit('delete-team')">
          <i class="fa-solid fa-trash"></i>
          Delete Team
        </button>
      </div>
    </div>
    
    <!-- Dashboard Tabs -->
    <div class="dashboard-tabs">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        class="tab-btn"
        :class="{ 'active': activeTab === tab.id }"
        @click="$emit('tab-change', tab.id)"
      >
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
        <span v-if="tab.count !== undefined" class="tab-count">{{ tab.count }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TeamHeader',
  props: {
    team: {
      type: Object,
      required: true
    },
    userRole: {
      type: String,
      default: 'owner'
    },
    activeTab: {
      type: String,
      required: true
    },
    tabs: {
      type: Array,
      required: true
    }
  },
  emits: ['image-error', 'edit-team', 'invite-players', 'delete-team', 'tab-change']
};
</script>

<style scoped>
.dashboard-header {
  background: var(--card-bg);
  padding: 30px;
  margin-bottom: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.team-info-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 30px;
  margin-bottom: 30px;
  align-items: start;
}

@media (max-width: 1024px) {
  .team-info-header {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.team-logo-wrapper {
  position: relative;
}

.team-logo-large {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid var(--card-hover-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.team-logo-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--meta-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--card-hover-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  color: var(--section-icon);
  font-size: 3rem;
}

.team-details {
  flex: 1;
}

.header-title {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 800;
  margin-bottom: 15px;
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: var(--heading-color);
}

.team-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.team-type, .team-location, .captain-badge, .team-status {
  padding: 6px 16px;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.team-type {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.team-location {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.captain-badge {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #8B4513;
  border: 1px solid #FFA500;
}

.team-status.active {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.team-status.inactive {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.team-bio {
  color: var(--subtext-color);
  line-height: 1.6;
  margin-bottom: 20px;
}

.team-stats-quick {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.stat-item i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.stat-item strong {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--heading-color);
}

.stat-item span {
  font-size: 0.9rem;
  color: var(--subtext-color);
}

.team-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 200px;
}

@media (max-width: 1024px) {
  .team-actions {
    flex-direction: row;
    flex-wrap: wrap;
    min-width: auto;
  }
}

.btn-action {
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  width: 100%;
}

@media (max-width: 1024px) {
  .btn-action {
    width: auto;
    flex: 1;
  }
}

.edit-btn {
  background: var(--edit-gradient);
  color: var(--button-color);
  box-shadow: 0 4px 15px var(--edit-shadow);
}

.invite-btn {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.danger-btn {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.dashboard-tabs {
  display: flex;
  gap: 8px;
  background: var(--meta-bg);
  padding: 8px;
  border-radius: 16px;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-btn {
  position: relative;
  background: transparent;
  border: 1px solid transparent;
  color: var(--subtext-color);
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--heading-color);
}

.tab-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border-color: var(--tab-active-border);
}

.tab-count {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  color: var(--button-color);
  font-weight: 600;
}

.tab-btn:not(.active) .tab-count {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
}
</style>
